import Linked from 'shared-components/service/Linked'
import { publicConfig } from 'config'
import { firstReviewAwardLine, safeImageUrl, writeReviewHref } from 'lib/community/model'
import type { CommunityPoints, CommunityProduct } from 'lib/community/types'
import styles from './Feed.module.css'

/** How many waiting products the module shows. */
const SHOWN = 4

/**
 * The feed's interruption (design 8b): after a run of cards, one module that turns the list
 * into an action — "Own one of these? They have no owner review yet." with up to four of
 * DealHunt's `waiting_products` and what a first review earns ("+75 each": DealHunt's `review`
 * plus its `first_review_bonus`, the same sum the hub and the picker promise). Renders nothing
 * without products that can be reviewed. The member hub's Gear tab reuses it for the member's own
 * candidates (design 1b) with its own heading and chip. The design's other rotations — the Founding 100
 * progress (already the panel over this feed) and open questions (phase 3) — are not here.
 */
const FeedInterruption: React.VFC<{
  products: CommunityProduct[]
  points: CommunityPoints | null
  /** The heading; the member's Gear tab says "Own one of these? No owner review yet." (1b). */
  title?: string
  /** The points chip when the host words it (`gearCandidatesAward`): null = no chip. Absent =
   *  the feed's "+N each" from `points`. */
  award?: string | null
}> = ({ products, points, title = 'Own one of these? They have no owner review yet.', award }) => {
  const trailingSlash = publicConfig('useTrailingSlash')
  const rows = products
    .flatMap(product => {
      const write = writeReviewHref(product, trailingSlash)
      return write ? [{ product, write, image: safeImageUrl(product.image_url) }] : []
    })
    .slice(0, SHOWN)
  const chip = award === undefined ? firstReviewAwardLine(points) : award
  if (!rows.length) {
    return null
  }
  return (
    <section className={styles.interrupt} aria-labelledby="interrupt-title">
      <div className={styles.interruptHead}>
        <h3 id="interrupt-title" className={styles.interruptTitle}>
          {title}
        </h3>
        {chip && <span className={styles.interruptPts}>{chip}</span>}
      </div>
      <ul className={styles.interruptList}>
        {rows.map(({ product, write, image }) => (
          <li key={product.node_id} className={styles.interruptCard}>
            <Linked href={write} className={styles.interruptThumb}>
              {image && (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={image} alt="" loading="lazy" />
              )}
            </Linked>
            <span className={styles.interruptName}>{product.name}</span>
            <Linked href={write} className={styles.interruptOwn}>
              {'I own it'}
            </Linked>
          </li>
        ))}
      </ul>
    </section>
  )
}

export default FeedInterruption
